import { CeLevelMenuExtended, RouteExtended } from './../types/route-types';

const trimSlashes = (s: string) => {
    let start = 0,
        end = s.length;
    while (start < end && s.charCodeAt(start) === 47) start++; // '/'
    while (end > start && s.charCodeAt(end - 1) === 47) end--;
    return s.slice(start, end);
};

const joinPaths = (parent?: string, child?: string) => {
    const p = trimSlashes(parent ?? '');
    const c = trimSlashes(child ?? '');
    if (!p && !c) return ''; // both empty → index at root
    if (!p) return c; // only child
    if (!c) return p; // index/empty child → parent's full path
    return `${p}/${c}`; // parent/child
};

/**
 * Builds a side navigation menu structure from an array of authenticated routes.
 *
 * Each route is transformed into a `CeLevelMenu` item, including its label, path, icon (if present),
 * and any child routes recursively. Routes without required data are excluded from the menu.
 *
 * @param authenticatedRoutes - An array of `RouteExtended` objects representing the authenticated routes.
 * @returns An array of `CeLevelMenu` items representing the side navigation structure.
 */
export function buildSideNav(authenticatedRoutes: RouteExtended[]): CeLevelMenuExtended[] {
    const toMenu = (route?: RouteExtended, parentFullPath = ''): CeLevelMenuExtended | null => {
        if (!route?.data) return null;

        const { data, path, children } = route;

        const computedFull = joinPaths(parentFullPath, path);
        const fullPath = computedFull || parentFullPath || '404';

        const item: CeLevelMenuExtended = {
            label: data?.['sideNavTitle'] ?? 'Title not set',
            secondaryLabel: data?.secondaryLabel ?? null,
            path: path ?? '404',
            fullPath, // full path from root
            ...(data?.icon && { icon: data.icon as CeLevelMenuExtended['icon'] }),
            data,
        };

        const childMenus = children
            ?.map((child) => toMenu(child, fullPath)) // pass current fullPath downward
            .filter(Boolean) as CeLevelMenuExtended[] | undefined;

        if (childMenus?.length) item.children = childMenus;

        return item;
    };

    return (authenticatedRoutes ?? []).map((r) => toMenu(r, '')).filter(Boolean) as CeLevelMenuExtended[];
}
